<template>
  <div class="chometz-company">
    <ChometzSalesInfoModal
      v-if="showInfoModal"
      :startDate="startDateFormatted"
      :endDate="endDateFormatted"
      @closeModal="showInfoModal = false"
    />
    <header class="chometz-sale-header">
      <h2>Chometz Sale Form</h2>
      <font-awesome-icon
        class="question-icon"
        icon="fa-solid fa-circle-question"
        @click="showInfoModal = true"
        data-cy="product-name-tooltip"
      />
    </header>
    <h4
      v-if="isPlantMode && remainingPlantCount > 0"
      class="chometz-plant-label"
    >
      Please select a plant(s) to fill out this form. You have
      {{
        loadingPlants
          ? "--"
          : remainingPlantCount > 0
          ? remainingPlantCount
          : "no"
      }}
      plant(s) remaining without a Chometz Sale designation for
      {{ chometzPeriod?.start.getFullYear() }}.
    </h4>
    <DropDownList
      v-if="isPlantMode"
      id="plant-name"
      name="plant-name"
      class="chometz-plant-dropdown"
      :dataItems="chometzPlants"
      v-model="selectedPlant"
      :loading="loadingPlants"
      text-field="Name"
      data-item-key="Id"
      @change="onPlantSelected"
    />
    <h4>Account Name: {{ main.companySummary?.Name }}</h4>
    <h4>Account Administrator: Rabbi {{ main.companySummary?.RcName }}</h4>
    <h4>Account Administrator Email: {{ main.companySummary?.RcEmail }}</h4>
    <h4>Account Administrator Phone: {{ main.companySummary?.RcPhone }}</h4>
    <h4>Chometz Sale Administrator: Rabbi Nachum Rabinowitz</h4>
    <h4 v-if="plantSelected">Plant Name: {{ selectedPlant.Name }}</h4>
    <br />
    <p>
      Passover Sale: {{ startDateFormatted }} through {{ endDateFormatted }}
    </p>
    <br />
    <div v-if="finalizedOrNoChometz" class="finalized-message">
      Chometz form cannot be submitted. Please contact your RC.
    </div>
    <form
      v-if="!finalizedOrNoChometz && (!isPlantMode || selectedPlant.Id > 0)"
      class="chometz-company-form"
      @submit.prevent="onSubmitAsync"
    >
      <p>
        My name is
        <input
          type="text"
          v-model="userName"
          :minlength="3"
          required
          placeholder="Enter Your Full Name"
        />
      </p>
      <br /><br />
      Select one of the options below.
      <br />
      <hr />
      <div class="options">
        <label>
          <input type="radio" value="ou" v-model="companyOption" />
          <span>
            I certify that I am authorized to act as my company’s agent to
            arrange the sale of Chometz for the duration of Passover
            {{ chometzPeriod?.start.getFullYear() }}.
            <br />
            I hereby appoint Rabbi Nachum Rabinowitz, or his appointed
            substitute to act as our legal agent to sell our Chometz. for the
            transfer and sale of all chometz and chometz products in the
            possession of the company in any of the locations below, as well as
            any other location even if unlisted. Rabbi Rabinowitz is empowered
            to use any legal and halachic (Jewish law) procedures which he deems
            necessary to effect these transactions and for such duration as to
            conform to halachic (Jewish law) requirements. This authorization is
            in conformity with all Torah and Rabbinic laws and with the laws of
            the State of New York, and of the United States of America. Included
            in this authorization is the right to rent any areas where any
            chometz may be found. The sale shall take place on or before
            {{ startDateFormatted }} until the end of Passover on
            {{ endDateFormatted }}.
            <b
              >See additional details below regarding unlisted facilities and
              time of sale.</b
            >
          </span>
        </label>
        <label>
          <input
            type="radio"
            value="other"
            v-model="companyOption"
            style="margin-top: 10px"
          />
          <span>
            We have or will arrange an independent sale of our Chometz through
            <input
              type="text"
              v-model="otherSaleComment"
              :minlength="3"
              :required="companyOption == 'other'"
              placeholder="Type name"
            />
          </span>
        </label>
      </div>

      <div class="upload-container-wrapper">
        <p>
          <i
            >Upload document from Rabbi confirming sale of Chometz will take
            place</i
          >
        </p>
        <header class="form-header">
          <span data-cy="documentation-header-title">Add Documents</span>
        </header>

        <FileUpload v-model="uploadedDocs" />
      </div>
      <hr />
      <div class="additional-comments-container">
        <label>
          <br />
          <b>DETAILS OF CHOMETZ SALE THROUGH THE OU </b> <br />
        </label>
        <div v-if="!isPlantMode" class="plants-table-container">
          <ChometzSalesPlantsTable />
        </div>
        <p v-if="isPlantMode">Comments</p>
        <p v-else>
          The plant list above would be covered by any Chometz sale. Enter all
          additional facility addresses below where there is chometz to be sold,
          if not listed above.
        </p>
        <textarea
          id="additional-comments-facilities"
          name="additional-comments-facilities"
          v-model="additionalCommentsFacilities"
          :rows="5"
          :maxlength="500"
        ></textarea>

        <label>
          Select a time zone below if the company owner will be or your company
          is east of NY at the time of the sale as shown above.
        </label>
        <DropDownList
          id="time-zone"
          name="time-zone"
          :dataItems="filteredTimeZones"
          v-model="selectedTimeZone"
          text-field="Name"
          data-item-key="Value"
          :filterable="true"
          @filterchange="onTimeZoneFilterChange"
        />
        <div v-if="main.isEmulator" class="jotform-checkbox-wrapper">
          <input
            type="checkbox"
            id="jotform-checkbox"
            class="jotform-checkbox"
            v-model="isJotform"
          />
          <label
            class="jotform-label"
            for="jotform-checkbox"
            data-cy="jotform-label"
            >Is Jotform Submission</label
          >
        </div>
      </div>

      <div>
        Contact your Rabbinic Coordinator with any questions or comments: <br />

        Rabbi {{ main.companySummary?.RcName }} <br />
        {{ main.companySummary?.RcEmail }} <br />
        {{ main.companySummary?.RcPhone }} <br /><br />
      </div>
      <Button
        themeColor="primary"
        type="submit"
        :disabled="
          formSubmitting || !main.companySummary || finalizedOrNoChometz
        "
        >Submit</Button
      >
    </form>
  </div>
</template>

<script setup lang="ts">
import { EnumChometzSaleOption } from "@/Domain/EnumChometzSaleOption";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { Button } from "@progress/kendo-vue-buttons";
import { computed, onBeforeMount, ref, watch } from "vue";
import ChometzSalesInfoModal from "./ChometzSalesInfoModal.vue";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import ChometzSalesPlantsTable from "./ChometzSalesPlantsTable.vue";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";
import type { Facility } from "@/Domain/Facility";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import type { NameValue } from "@/Domain/NameValue";
import { CacheService } from "@/shared/services/CacheService";
import moment from "moment";
import { filterBy } from "@progress/kendo-data-query";
import type { EnumTimeZone } from "@/Domain/EnumTimeZone";
import ChometzAttachmentsView from "./ChometzAttachmentsView.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const emit = defineEmits<{
  (e: "submitted"): void;
  (e: "plantChanged", plant: Facility): void;
}>();

defineProps<{
  finalizedOrNoChometz: boolean;
  plantId?: number;
}>();

const companyOption = ref("ou");
const otherSaleComment = ref("");
const additionalCommentsFacilities = ref("");
const additionalCommentsTimeOfSale = ref("");
const formSubmitting = ref(false);
const userName = ref("");
const showInfoModal = ref(false);
const uploadedDocs = ref<UploadFileInfo[]>([]);
const isJotform = ref(false);

const plants = ref<Facility[]>([]);
const defaultPlantValue = {
  Id: 0,
  Name: "Select a Plant",
} as any as Facility;
const selectedPlant = ref<Facility>(defaultPlantValue);
const loadingPlants = ref(false);

const timeZones = ref<NameValue[]>([]);
const filteredTimeZones = ref<NameValue[]>([]);
const selectedTimeZone = ref<NameValue | null>();

const chometzPeriod = ref<{
  start: Date;
  end: Date;
} | null>(null);

const startDateFormatted = computed(() => {
  return chometzPeriod.value
    ? moment(chometzPeriod.value.start).format("MMMM D, YYYY [at] h:mmA [ET]")
    : "-----";
});

const endDateFormatted = computed(() => {
  return chometzPeriod.value
    ? moment(chometzPeriod.value.end).format("MMMM D, YYYY [at] h:mmA [ET]")
    : "-----";
});

const chometzPlants = computed(() => {
  return [defaultPlantValue, ...plants.value];
});

const remainingPlantCount = computed(() => {
  return plants.value.filter((p) => !p.ChometzSubmitted).length;
});

const isPlantMode = computed(
  () =>
    !main.companySummary?.JewishOwned && main.companySummary?.HasJewishPlants
);

const plantSelected = computed(
  () => isPlantMode.value && selectedPlant.value.Id > 0
);

const resetForm = function () {
  companyOption.value = "ou";
  otherSaleComment.value = "";
  additionalCommentsFacilities.value = "";
  additionalCommentsTimeOfSale.value = "";
  userName.value = "";
  uploadedDocs.value = [];
};

const getPlantsAsync = async function () {
  loadingPlants.value = true;

  await handleResponsePromiseAsync(
    api.get("GetCompanyJewishPlants", {
      companyLogin: main.companyLogin,
    }),
    (data) => {
      plants.value = data;
    },
    "ChometzCompanyForm.vue",
    "getPlantsAsync",
    () => {
      notificationStore.notify({
        message: `Error loading plants`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  loadingPlants.value = false;
};

const getTimeZones = async function () {
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync("GetTimeZones", () =>
      api.get("GetTimeZones", {})
    ),
    (data) => {
      timeZones.value = [...data];
      filteredTimeZones.value = [...data];
      selectedTimeZone.value = timeZones.value[0];
    },
    "ChometzCompanyForm.vue",
    "getTimeZones",
    () => {
      notificationStore.notify({
        message: `Time Zone Error Encountered`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const getChometzSalePeriod = async function () {
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync("GetChometzSalePeriod", () =>
      api.get("GetChometzSalePeriod", { year: 0 })
    ),
    (data) => {
      chometzPeriod.value = {
        start: new Date(data.Item1),
        end: new Date(data.Item2),
      };
    },
    "ChometzCompanyForm.vue",
    "getChometzPeriod",
    () => {
      notificationStore.notify({
        message: `Error loading Chometz Sale Period`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const onPlantSelected = function () {
  emit("plantChanged", selectedPlant.value);
};

const onTimeZoneFilterChange = function (event: any) {
  filteredTimeZones.value = filterBy([...timeZones.value], event.filter);
};

const onSubmitAsync = async function () {
  if (formSubmitting.value) return;

  if (isPlantMode.value && selectedPlant.value == defaultPlantValue) {
    notificationStore.notify({
      message: `Please select a plant`,
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  formSubmitting.value = true;

  // Validate total files size
  if (!validateTotalUploadSize(uploadedDocs.value)) {
    formSubmitting.value = false;
    return;
  }

  const files = await getAttachmentFilesAsync(uploadedDocs.value);

  var EmailComment =
    additionalCommentsFacilities.value > ""
      ? "<br>Additional Facilities: " + additionalCommentsFacilities.value
      : "";
  EmailComment +=
    additionalCommentsTimeOfSale.value > ""
      ? "<br>Time of Sale: " + additionalCommentsTimeOfSale.value
      : "";

  const submitPromise = api.post("SubmitChometzSale", {
    CompanyLogin: !isPlantMode.value ? main.companyLogin : "",
    PlantId: isPlantMode.value ? selectedPlant.value.Id : 0,
    SaleOption:
      companyOption.value == "ou"
        ? EnumChometzSaleOption.OUSale
        : EnumChometzSaleOption.NonOUSale,
    AdditionalFacilities: additionalCommentsFacilities.value,
    RabbiName: otherSaleComment.value,
    SignatoryName: userName.value,
    Timezone: selectedTimeZone.value
      ? (Number(selectedTimeZone.value.Value) as EnumTimeZone)
      : null,
    Name: userName.value,
    Files: files,
    IsJotform: isJotform.value,
  });

  await handleResponsePromiseAsync(
    submitPromise,
    (data) => {
      if (data) {
        notificationStore.notify({
          message: "Chometz Sales Form Submitted Successfully",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
        if (isPlantMode.value) {
          selectedPlant.value.ChometzSubmitted = true;
        }
        resetForm();
        emit("submitted");
      } else {
        notificationStore.notify({
          message: `Error submitting Chometz Sales Form. Please try again.`,
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
      }
    },
    "ChometzCompanyForm.vue",
    "onSubmit",
    (error) => {
      notificationStore.notify({
        message: `Error submitting Chometz Sales Form. Please try again.`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  formSubmitting.value = false;
};

watch(isPlantMode, () => {
  if (isPlantMode.value) getPlantsAsync();
});

onBeforeMount(() => {
  if (isPlantMode.value) getPlantsAsync();
  getTimeZones();
  getChometzSalePeriod();
});
</script>

<style scoped lang="scss">
.chometz-company {
  max-width: 800px;
  margin: 0px auto;

  .finalized-message {
    font-weight: bold;
    color: #337ab7;
  }

  .chometz-plant-label {
    font-size: 14px;
    margin-bottom: 10px;
    color: #337ab7;
  }

  .chometz-plant-dropdown {
    margin-bottom: 16px;
    max-width: 320px;
  }

  .jotform-checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;

    .jotform-checkbox {
      width: 18px;
      height: 18px;
    }
  }
}
header.chometz-sale-header {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  margin-bottom: 15px;
  .question-icon {
    color: #399cd3;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
}
.chometz-company-form {
  background: white;
  padding: 40px 20px;
  border-radius: 6px;
  box-shadow: 4px 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;

  P {
    font-size: 14px;
  }
}

.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  margin-bottom: 10px;

  label {
    margin-bottom: 10px;
    font-size: 14px;
  }
}
.btn {
  max-width: 200px;
}

.upload-container-wrapper {
  margin-bottom: 20px;
  label {
    font-weight: bold;
  }
}
.form-header {
  margin-top: 20px;
  font-weight: bolder;
}
input[type="text"] {
  background: #f8f9fa;
}
.additional-comments-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
  label {
    font-weight: bold;
  }
  textarea {
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #f8f9fa;
    padding: 10px;
    font-size: 14px;
    resize: vertical;
  }
}

.plants-table-container {
  margin-bottom: 20px;
}
</style>
